import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Route, Users, MapPin, Lightbulb, AlertCircle, CheckCircle2 } from 'lucide-react';

export const ManualRoutingGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Manual Routing'
      description='Assign recalls to technicians and create optimized routes'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Manual Routing' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* Hero Section */}
        <section className='mb-20'>
          <div className='not-prose'>
            <Card className='border-primary/50 bg-primary/5 rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Route className='h-8 w-8 text-primary' />
                  </div>
                  <div>
                    <CardTitle className='text-lg font-bold'>What is Manual Routing?</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Manual Routing is where you assign recalls to technicians and create routes. The
                  system provides AI-powered suggestions based on skills, location, and workload,
                  but you maintain full control over final assignments. This feature helps you
                  optimize mobile service routes, balance technician workloads, and ensure the right
                  technician is matched to each recall.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Who Can Access Manual Routing</h2>
          <p className='text-sm text-muted-foreground mb-6 leading-6'>
            Manual Routing is available to users with these roles:
          </p>
          <ul className='space-y-2 text-sm leading-6'>
            <li>Service Advisors</li>
            <li>Service Managers</li>
            <li>Field Operations Directors</li>
            <li>Lead Technicians</li>
            <li>Dealership Admins</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Accessing Manual Routing</h2>
          <p className='text-sm text-muted-foreground mb-4 leading-6'>
            Navigate to Manual Routing from the sidebar:
          </p>
          <p className='text-sm leading-6'>
            <strong>Sidebar → PLAN → Manual Routing</strong>
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>The Assignment Process</h2>

          <div className='not-prose'>
            <div className='space-y-4'>
              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge>Step 1</Badge>
                    <CardTitle className='text-lg font-semibold'>Select Recalls</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Use the <Link to='/docs/features/recall-map'>Recall Map</Link> or{' '}
                    <Link to='/docs/features/recall-list'>Recall List</Link> to select recalls for
                    assignment. You should have recalls selected before navigating to Manual
                    Routing.
                  </p>
                </CardContent>
              </Card>

              <Card className='border-primary/50 rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge variant='default'>Step 2</Badge>
                    <CardTitle className='text-lg font-semibold'>Assign to Technicians</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='font-semibold text-sm leading-6 mb-2'>This is where you are now!</p>
                  <p className='text-sm leading-6'>
                    Use the Manual Routing page to assign selected recalls to technicians.
                    Assignments save automatically as you make them.
                  </p>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge>Step 3</Badge>
                    <CardTitle className='text-lg font-semibold'>Track Progress</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Monitor technician progress and update recall statuses using the{' '}
                    <Link to='/docs/features/route-tracker'>Route Tracker</Link> page.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Manual Routing Interface</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The Manual Routing page has a unified layout with these key areas:
          </p>

          <div className='not-prose'>
            <div className='space-y-6'>
              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>Top: Technician Selector</CardTitle>
                </CardHeader>
                <CardContent className='text-sm space-y-1 leading-6'>
                  <div>• Dropdown to choose which technician you're assigning to</div>
                  <div>• Shows technician name and current workload</div>
                  <div>• Changes which recalls are shown below</div>
                </CardContent>
              </Card>

              <div className='grid gap-6 md:grid-cols-2'>
                <Card className='rounded-xl border shadow-sm'>
                  <CardHeader className='pb-4'>
                    <CardTitle className='text-lg font-semibold'>Left Side (55%)</CardTitle>
                    <CardDescription>Recall Assignment List</CardDescription>
                  </CardHeader>
                  <CardContent className='text-sm space-y-1 leading-6'>
                    <div>• List of recalls available for assignment</div>
                    <div>• Shows only unassigned recalls by default</div>
                    <div>• Individual assignment controls for each recall</div>
                    <div>• Bulk selection checkboxes</div>
                    <div>• Auto-save when assignments made</div>
                  </CardContent>
                </Card>

                <Card className='rounded-xl border shadow-sm'>
                  <CardHeader className='pb-4'>
                    <CardTitle className='text-lg font-semibold flex items-center gap-2'>
                      <MapPin className='h-4 w-4' />
                      Right Side (45%)
                    </CardTitle>
                    <CardDescription>Route Preview Map</CardDescription>
                  </CardHeader>
                  <CardContent className='text-sm space-y-1 leading-6'>
                    <div>• Shows dealer locations for current technician's assignments</div>
                    <div>• Updates live as you make assignments</div>
                    <div>• Route optimization and distance calculations</div>
                    <div>• Visual feedback of geographic distribution</div>
                  </CardContent>
                </Card>
              </div>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>
                    Bottom: Bulk Assignment Bar
                  </CardTitle>
                </CardHeader>
                <CardContent className='text-sm space-y-1 leading-6'>
                  <div>• Appears when multiple recalls are selected</div>
                  <div>• Set date and notes for all selected recalls at once</div>
                  <div>• Faster than individual assignments for grouped work</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Understanding Recall Cards</h2>
          <p className='text-sm text-muted-foreground mb-6 leading-6'>
            Each recall in the left panel displays:
          </p>
          <ul className='space-y-2 text-sm leading-6'>
            <li>
              <strong>VIN:</strong> Last 8 digits (for space efficiency)
            </li>
            <li>
              <strong>Vehicle:</strong> Year Make Model (e.g., "2020 Honda Accord")
            </li>
            <li>
              <strong>Campaign:</strong> Campaign number
            </li>
            <li>
              <strong>Dealer:</strong> Name and city
            </li>
            <li>
              <strong>Skill Required:</strong> Badge showing High/Medium/Low
            </li>
            <li>
              <strong>Mobile:</strong> ✅ if mobile-eligible, ❌ if facility-only
            </li>
            <li>
              <strong>Assignment Status:</strong> Blank if unassigned, or technician name + date if
              assigned
            </li>
          </ul>

          <h3 className='text-xl font-bold mt-10 mb-4'>Card Actions</h3>
          <ul className='space-y-2 text-sm leading-6'>
            <li>Click card to select for individual assignment</li>
            <li>Check checkbox for batch assignment</li>
            <li>Click expand button to view full details</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>How to Make Assignments</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The current interface uses a technician-first approach where you choose the technician
            first, then assign recalls to them:
          </p>

          <div className='not-prose'>
            <div className='space-y-4'>
              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge variant='outline'>1</Badge>
                    <CardTitle className='text-lg font-semibold'>Select Technician</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Use the dropdown at the top to choose which technician you want to assign work
                    to. The list shows only unassigned recalls that are available for assignment.
                  </p>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge variant='outline'>2</Badge>
                    <CardTitle className='text-lg font-semibold'>
                      Choose Individual Recalls
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 mb-3'>
                    For each recall you want to assign to this technician:
                  </p>
                  <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                    <li>
                      • Set the <strong>expected visit date</strong> (required)
                    </li>
                    <li>
                      • Add <strong>notes</strong> if needed (optional)
                    </li>
                    <li>
                      • Click <strong>"Assign"</strong> button
                    </li>
                    <li>• Assignment saves automatically</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge variant='outline'>3</Badge>
                    <CardTitle className='text-lg font-semibold'>Monitor Route Preview</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Watch the map on the right side update in real-time as you make assignments.
                    This helps you see the geographic distribution and optimize the route.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Bulk Assignment</h2>

          <div className='not-prose mb-10'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Best For:</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6'>
                  <li>Geographic clustering (all recalls in same area)</li>
                  <li>Assigning multiple recalls to the same technician</li>
                  <li>Setting the same date and notes for multiple recalls</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <h3 className='text-xl font-bold mb-6'>Bulk Assignment Steps</h3>

          <div className='not-prose'>
            <div className='space-y-4'>
              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge variant='outline'>1</Badge>
                    <CardTitle className='text-lg font-semibold'>Choose Technician First</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Select the technician from the dropdown at the top of the page. This determines
                    who will receive the bulk assignment.
                  </p>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge variant='outline'>2</Badge>
                    <CardTitle className='text-lg font-semibold'>Select Multiple Recalls</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 mb-3'>
                    Use the checkboxes to select recalls for bulk assignment:
                  </p>
                  <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                    <li>• Check individual recall checkboxes</li>
                    <li>• A bulk assignment bar appears at the bottom</li>
                    <li>• Shows count of selected recalls</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge variant='outline'>3</Badge>
                    <CardTitle className='text-lg font-semibold'>Set Assignment Date</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 mb-3'>In the bulk assignment bar:</p>
                  <ul className='space-y-2 text-sm leading-6 text-muted-foreground'>
                    <li>
                      • Set the <strong>expected visit date</strong> (required)
                    </li>
                    <li>• Date applies to all selected recalls</li>
                    <li>• Technician name shown for confirmation</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-2'>
                    <Badge variant='outline'>4</Badge>
                    <CardTitle className='text-lg font-semibold'>Confirm Assignment</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className='space-y-2 text-sm leading-6'>
                    <li>• Click "Assign" in the bulk assignment bar</li>
                    <li>• All selected recalls assigned to chosen technician</li>
                    <li>• Assignments save automatically</li>
                    <li>• Map updates to show new route</li>
                    <li>• Selection clears after successful assignment</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Technician Selection Guidance</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Consider these factors when choosing which technician to assign recalls to:
          </p>

          <div className='not-prose'>
            <div className='space-y-4'>
              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>Skill Level Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Match technician skills to recall complexity. High skill recalls should go to
                    experienced technicians. Low skill recalls can be handled by any technician.
                  </p>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>Geographic Location</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Assign recalls to technicians who live or work near the dealer location. This
                    minimizes travel time and maximizes efficiency.
                  </p>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>Current Workload</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Check each technician's current assignment count. Balance the workload across
                    your team to prevent anyone from being overwhelmed.
                  </p>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>Vehicle Specializations</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Some technicians may be more familiar with certain vehicle makes or types of
                    recalls. Consider their experience and expertise.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>When to Override AI Suggestions</h2>

          <div className='not-prose'>
            <Card className='border-blue-500/50 bg-blue-500/5 rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-bold text-blue-700 dark:text-blue-400'>
                  You Can Always Override
                </CardTitle>
                <CardDescription className='text-sm'>
                  The system provides suggestions, not requirements. You know your team and
                  circumstances best!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className='space-y-4 text-sm leading-6'>
                  <div>
                    <strong>1. Technician Unavailable</strong>
                    <p className='text-muted-foreground mt-1'>
                      Suggestions don't know about PTO or sick days. If you know a technician is
                      out, choose the next best option.
                    </p>
                  </div>
                  <div>
                    <strong>2. Customer Relationship</strong>
                    <p className='text-muted-foreground mt-1'>
                      Customer specifically requested a technician, or had an issue with the
                      suggested technician. Build technician-customer relationships.
                    </p>
                  </div>
                  <div>
                    <strong>3. Training Opportunity</strong>
                    <p className='text-muted-foreground mt-1'>
                      Assign complex recall to medium-skill tech supervised by lead technician for
                      skill development.
                    </p>
                  </div>
                  <div>
                    <strong>4. Known Efficiency</strong>
                    <p className='text-muted-foreground mt-1'>
                      Suggestions are based on general data, but you know a specific technician is
                      2x faster on a particular campaign. Real-world experience overrides the
                      algorithm.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Setting Expected Visit Dates Strategically</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The expected visit date drives route planning, manages customer expectations, and
            affects performance metrics. Here are some best practices:
          </p>

          <h3 className='text-xl font-bold mb-4'>Consider These Factors</h3>
          <ul className='space-y-2 text-sm leading-6 mb-10'>
            <li>✓ Technician's schedule (availability)</li>
            <li>✓ Customer's availability (if known)</li>
            <li>✓ Geographic clustering (group nearby stops same day)</li>
            <li>✓ Recall priority (high priority = sooner)</li>
            <li>✓ Realistic timeframes (don't overpack schedules)</li>
          </ul>

          <h3 className='text-xl font-bold mb-6'>Proven Strategies</h3>

          <div className='not-prose'>
            <div className='space-y-4'>
              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>Strategy 1: Zone Days</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-1 text-sm leading-6'>
                    <div>Monday: North zone</div>
                    <div>Tuesday: East zone</div>
                    <div>Wednesday: West zone</div>
                    <div>Thursday: South zone</div>
                    <div>Friday: Downtown zone</div>
                  </div>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>Strategy 2: Campaign Days</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Assign all recalls for the same campaign to the same day. This creates
                    efficiency as the technician becomes expert on that campaign, leading to faster
                    completions.
                  </p>
                </CardContent>
              </Card>

              <Card className='rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold'>
                    Strategy 3: Balanced Loading
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Don't pack 15 stops in one day. Realistic goal: 8-12 stops per day for mobile
                    service. Allow buffer time for delays.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Adding Effective Notes</h2>

          <div className='not-prose'>
            <div className='grid gap-6 md:grid-cols-2'>
              <Card className='border-green-500/50 bg-green-500/5 rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-bold text-green-700 dark:text-green-400'>
                    Good Notes Examples
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-2'>
                  <div className='p-2 bg-background rounded text-sm leading-6 font-mono'>
                    "Customer works from home, available anytime"
                  </div>
                  <div className='p-2 bg-background rounded text-sm leading-6 font-mono'>
                    "Call 30 min before arrival: (305) 555-1234"
                  </div>
                  <div className='p-2 bg-background rounded text-sm leading-6 font-mono'>
                    "Use rear entrance, front gate locked"
                  </div>
                  <div className='p-2 bg-background rounded text-sm leading-6 font-mono'>
                    "Park on street, not driveway (narrow)"
                  </div>
                  <div className='p-2 bg-background rounded text-sm leading-6 font-mono'>
                    "Customer speaks Spanish only"
                  </div>
                  <div className='p-2 bg-background rounded text-sm leading-6 font-mono'>
                    "Parts pre-ordered, ready in service dept"
                  </div>
                </CardContent>
              </Card>

              <Card className='border-red-500/50 bg-red-500/5 rounded-xl border shadow-sm'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-bold text-red-700 dark:text-red-400'>
                    Bad Notes Examples
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-2'>
                  <div className='p-2 bg-background rounded'>
                    <div className='font-mono text-sm leading-6'>"Important"</div>
                    <div className='text-xs text-muted-foreground mt-1'>(not specific)</div>
                  </div>
                  <div className='p-2 bg-background rounded'>
                    <div className='font-mono text-sm leading-6'>"Call customer"</div>
                    <div className='text-xs text-muted-foreground mt-1'>(missing phone number)</div>
                  </div>
                  <div className='p-2 bg-background rounded'>
                    <div className='font-mono text-sm leading-6'>"ASAP"</div>
                    <div className='text-xs text-muted-foreground mt-1'>
                      (use expected date instead)
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Monitoring Assignment Progress</h2>

          <h3 className='text-xl font-bold mb-4'>Real-Time Updates</h3>
          <p className='text-sm text-muted-foreground mb-4 leading-6'>
            The page automatically refreshes every 2 seconds to show:
          </p>
          <ul className='space-y-2 text-sm leading-6 mb-10'>
            <li>Updated assignment counts</li>
            <li>Status badge updates</li>
            <li>New assignments from other users</li>
          </ul>

          <h3 className='text-xl font-bold mb-4'>Status Indicators</h3>
          <ul className='space-y-2 text-sm leading-6 mb-10'>
            <li>⚪ Unassigned: Ready to assign</li>
            <li>🟡 Pending: Assignment in progress</li>
            <li>🟢 Assigned: Assignment complete, showing tech name</li>
          </ul>

          <h3 className='text-xl font-bold mb-4'>Workload Counters</h3>
          <p className='text-sm leading-6'>
            Each technician card shows their current assignment count (e.g., "8 assignments"). This
            updates as you assign, helping you balance workload across the team.
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Reassignment</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            You can reassign recalls when needed:
          </p>

          <h3 className='text-xl font-bold mb-4'>When to Reassign</h3>
          <ul className='space-y-2 text-sm leading-6 mb-10'>
            <li>Technician called in sick</li>
            <li>Customer requested different tech</li>
            <li>Original tech overloaded</li>
            <li>Geographic optimization</li>
          </ul>

          <h3 className='text-xl font-bold mb-4'>How to Reassign</h3>
          <ol className='space-y-2 text-sm leading-6 mb-6'>
            <li>Find recall in list (filter by technician)</li>
            <li>Click recall card</li>
            <li>Click "Reassign" button</li>
            <li>Choose new technician</li>
            <li>Update date if needed</li>
            <li>Add note explaining reason</li>
            <li>Confirm</li>
          </ol>
          <p className='text-sm leading-6'>
            <strong>Note:</strong> Original assignment history is saved in the audit log.
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Common Mistakes to Avoid</h2>

          <div className='not-prose'>
            <Card className='border-yellow-500/50 bg-yellow-500/5 rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='flex items-center gap-2 text-lg font-bold text-yellow-700 dark:text-yellow-400'>
                  <AlertCircle className='h-8 w-8' />
                  Watch Out For These
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-4 text-sm leading-6'>
                  <li className='flex items-start gap-2'>
                    <span className='text-red-600 mt-0.5'>❌</span>
                    <div>
                      <strong>Forgetting Expected Visit Date:</strong> This is a REQUIRED field. The
                      assignment won't save without it.
                    </div>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-red-600 mt-0.5'>❌</span>
                    <div>
                      <strong>Overloading One Technician:</strong> Check workload counts and balance
                      assignments across the team.
                    </div>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-red-600 mt-0.5'>❌</span>
                    <div>
                      <strong>Ignoring Geography:</strong> Don't assign a technician 50 miles from
                      the dealer. Use the map to visualize.
                    </div>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-red-600 mt-0.5'>❌</span>
                    <div>
                      <strong>Ignoring Skill Levels:</strong> Don't assign High skill recalls to Low
                      skill technicians. The system prevents this, but be aware.
                    </div>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-red-600 mt-0.5'>❌</span>
                    <div>
                      <strong>Not Adding Important Notes:</strong> Customer preferences get lost and
                      technicians may show up at the wrong time.
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Tips for Efficient Assignment</h2>

          <div className='not-prose'>
            <Card className='border-green-500/50 bg-green-500/5 rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='flex items-center gap-2 text-lg font-bold text-green-700 dark:text-green-400'>
                  <Lightbulb className='h-8 w-8' />
                  Best Practices
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className='space-y-2 text-sm leading-6'>
                  <li className='flex items-start gap-2'>
                    <span className='text-green-600 mt-0.5'>✓</span>
                    <span>Use batch assignment for geographic zones</span>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-green-600 mt-0.5'>✓</span>
                    <span>Trust AI suggestions (usually accurate)</span>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-green-600 mt-0.5'>✓</span>
                    <span>Balance workload across team</span>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-green-600 mt-0.5'>✓</span>
                    <span>Set realistic dates (don't overpromise)</span>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-green-600 mt-0.5'>✓</span>
                    <span>Add notes for anything unusual</span>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-green-600 mt-0.5'>✓</span>
                    <span>Review map before assigning (visualize route)</span>
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-green-600 mt-0.5'>✓</span>
                    <span>Consider technician specializations</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Next Steps</h2>
          <p className='text-sm text-muted-foreground mb-6 leading-6'>
            After assigning recalls, you can:
          </p>
          <ul className='space-y-2 text-sm leading-6'>
            <li>
              <Link to='/docs/features/route-tracker'>Track routes</Link> to monitor technician
              progress
            </li>
            <li>
              <Link to='/docs/features/recall-list'>Return to Recall List</Link> to select more
              recalls
            </li>
            <li>
              <Link to='/docs/features/technicians'>Manage Technicians</Link> to update skills and
              availability
            </li>
          </ul>
        </section>
      </div>
    </DocsLayout>
  );
};
